/* ---------------------------------------------------------------------------
   Cart recommendations.

   Fetches Shopify's Product Recommendations API keyed to a product already in
   the cart, so suggestions follow what the customer is actually buying. The
   API is per-product, not per-cart, so we anchor on the most recently added
   item — that's the strongest signal of current intent.

   When the section's source is set to a specific collection, the server has
   already rendered those products and no fetch happens.
--------------------------------------------------------------------------- */

async function loadCartRecommendations(root) {
  if (!root || root.dataset.source !== 'auto') return;
  if (root.dataset.loading === 'true') return;

  const anchorId = root.dataset.anchorProduct;
  if (!anchorId) {
    root.innerHTML = '';
    return;
  }

  // Skip the fetch if we already rendered for this anchor.
  if (root.dataset.loadedFor === anchorId) return;

  root.dataset.loading = 'true';

  const params = new URLSearchParams({
    product_id: anchorId,
    limit: root.dataset.limit || '8',
    intent: root.dataset.intent || 'related',
    section_id: root.dataset.sectionId,
  });

  try {
    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}recommendations/products?${params.toString()}`
    );
    if (!response.ok) throw new Error('Recommendations unavailable');

    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const fresh = doc.querySelector('[data-cart-recs]');

    if (fresh && fresh.innerHTML.trim()) {
      root.innerHTML = fresh.innerHTML;
      root.dataset.loadedFor = anchorId;
      bindRecsCarousel(root);
    } else {
      root.innerHTML = '';
    }
  } catch (error) {
    // Recommendations are an enhancement; failing quietly is correct.
    root.innerHTML = '';
  } finally {
    root.dataset.loading = 'false';
  }
}

// Arrow controls for the recommendation row.
function bindRecsCarousel(root) {
  const row = root.querySelector('[data-recs-row]');
  const prev = root.querySelector('[data-recs-prev]');
  const next = root.querySelector('[data-recs-next]');
  if (!row || !prev || !next) return;

  const step = () => {
    const first = row.querySelector('.cart-recs__item');
    if (!first) return row.clientWidth;
    const gap = parseFloat(getComputedStyle(row).gap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  const syncArrows = () => {
    const maxScroll = row.scrollWidth - row.clientWidth;
    prev.disabled = row.scrollLeft <= 1;
    next.disabled = row.scrollLeft >= maxScroll - 1;
  };

  prev.addEventListener('click', () => {
    row.scrollBy({ left: -step(), behavior: 'smooth' });
  });
  next.addEventListener('click', () => {
    row.scrollBy({ left: step(), behavior: 'smooth' });
  });
  row.addEventListener('scroll', syncArrows, { passive: true });

  syncArrows();
}

// Choose the anchor product: the last item in the cart, which is the most
// recently added and therefore the best signal of what the shopper wants now.
function setRecsAnchor(root, cart) {
  if (!cart || !cart.items || cart.items.length === 0) {
    root.dataset.anchorProduct = '';
    root.innerHTML = '';
    return;
  }
  const lastItem = cart.items[cart.items.length - 1];
  root.dataset.anchorProduct = String(lastItem.product_id);
}

async function refreshCartRecommendations() {
  const roots = document.querySelectorAll('[data-cart-recs][data-source="auto"]');
  if (roots.length === 0) return;

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    const cart = await response.json();
    roots.forEach((root) => {
      setRecsAnchor(root, cart);
      loadCartRecommendations(root);
    });
  } catch (error) {
    // Leave whatever is already rendered.
  }
}

document.addEventListener('DOMContentLoaded', () => {
  refreshCartRecommendations();
  document.querySelectorAll('[data-cart-recs][data-source="collection"]').forEach(bindRecsCarousel);
});

document.addEventListener('cart:updated', () => {
  setTimeout(refreshCartRecommendations, 100);
});
